<template>
  <pdl-dialog
    id="customization-summary-dialog"
    v-model:visible="isDialogVisible"
    :class="`customization-dialog-${summaryDialogView}`"
    :z-index="9999"
    :close-dialog-tool-tip="$t('popup.close')"
    qaid="customization-summary-dialog"
    top="5vh"
    @opened="focusTrapSummaryDialog"
    @keydown="handleKeydown"
    @close="closeDialog"
  >
    <template #title>
      <pdl-button v-if="!isBuildSummaryVisible" small text icon="arrow_back" @click="showBuildSummary">
        {{ $t('navigation.back') }}
      </pdl-button>
      <h2
        id="customization-summary-dialog-title"
        class="text-lg font-semibold"
        :class="[{'sr-only': isBuildSummaryVisible}]"
      >
        {{ dialogTitle }}
      </h2>
    </template>

    <customization-build-summary-body v-if="isBuildSummaryVisible" @body-mounted="focusTrapSummaryDialog" />
    <customization-submit-to-retailer-body v-if="isSubmitToRetailerVisible" @body-mounted="focusTrapSummaryDialog" />
    <customization-save-build-body v-if="isSaveBuildVisible" @body-mounted="focusTrapSummaryDialog" />
    <customization-share-email-body v-if="isEmailVisible" @body-mounted="focusTrapSummaryDialog" />
  </pdl-dialog>
</template>

<script>
import {PdlDialog} from '@pedal/pdl-dialog';
import {mapState, mapMutations} from 'vuex';
import CustomizationBuildSummaryBody from '@/components/containers/buy-zone/customization/CustomizationBuildSummaryBody.vue';
import CustomizationSubmitToRetailerBody from '@/components/containers/buy-zone/customization/CustomizationSubmitToRetailerBody.vue';
import CustomizationSaveBuildBody from '@/components/containers/buy-zone/customization/CustomizationSaveBuildBody.vue';
import CustomizationShareEmailBody from '@/components/containers/buy-zone/customization/CustomizationShareEmailBody.vue';
import {PdlButton} from '@pedal/pdl-button';
import {focusTrap, getAllFocusableElements} from '@/utils/accessibility/a11y-utils';
import {SUMMARY_DIALOG_VIEWS} from '@/familyDetailsPage/store/bike-customization.js';

export default {
  name: 'CustomizationSummaryDialog',
  components: {
    PdlDialog,
    PdlButton,
    CustomizationBuildSummaryBody,
    CustomizationSubmitToRetailerBody,
    CustomizationSaveBuildBody,
    CustomizationShareEmailBody,
  },
  data() {
    return {
      focusableElements: [],
    };
  },
  computed: {
    ...mapState('bikeCustomization', [
      'isCustomizationSummaryDialogOpen',
      'summaryDialogView',
      'cachedBuildId',
      'reopenPreviouslyOpenDialog',
      'isProcessingCustomizationAction',
    ]),
    ...mapState('backend', ['isUserLoggedIn']),

    isDialogVisible() {
      //check for cachedBuildId prevents flash of dialog while saved build data is fetched after login redirect
      return this.isCustomizationSummaryDialogOpen && !this.cachedBuildId;
    },

    dialogTitle() {
      if (this.isBuildSummaryVisible) {
        return this.$t('product.fdp.customizable.config.buildSummary');
      } else if (this.isSubmitToRetailerVisible) {
        return this.$t('projectOne.buildDreamBike');
      } else if (this.isSaveBuildVisible) {
        return this.$t('projectOne.config.save');
      } else if (this.isEmailVisible) {
        return this.$t('projectOne.config.summary.email');
      }
      return '';
    },

    isBuildSummaryVisible() {
      return this.summaryDialogView === SUMMARY_DIALOG_VIEWS.BUILD_SUMMARY;
    },

    isSubmitToRetailerVisible() {
      return this.summaryDialogView === SUMMARY_DIALOG_VIEWS.SUBMIT_TO_RETAILER;
    },

    isSaveBuildVisible() {
      return this.summaryDialogView === SUMMARY_DIALOG_VIEWS.SAVE_BUILD;
    },
    isEmailVisible() {
      return this.summaryDialogView === SUMMARY_DIALOG_VIEWS.SHARE_EMAIL;
    },
  },
  watch: {
    isDialogVisible(newVal) {
      if (newVal) {
        this.setReopenPreviouslyOpenDialog(false);
      }
      this.setIsDialogOpen(newVal);
    },
  },
  mounted() {
    if (this.isReopenPreviouslyOpenDialog && !this.isUserLoggedIn) {
      this.setSummaryDialogView(SUMMARY_DIALOG_VIEWS.BUILD_SUMMARY);
    } else if (!this.reopenPreviouslyOpenDialog) {
      this.setIsCustomizationSummaryDialogOpen(false);
    }
  },
  methods: {
    ...mapMutations('bikeCustomization', [
      'setIsCustomizationSummaryDialogOpen',
      'setSummaryDialogView',
      'setHasQueuedSaveAction',
      'setHasQueuedSubmitToRetailerAction',
      'setQueuedShareAction',
      'setReopenPreviouslyOpenDialog',
      'setIsDialogOpen',
    ]),

    closeDialog() {
      //ensure isCustomizationSummaryDialogOpen persists for login redirect
      if (!this.cachedBuildId) {
        this.setIsCustomizationSummaryDialogOpen(false);
        this.clearQueuedActions();
      }
      if (!this.isProcessingCustomizationAction) {
        this.setReopenPreviouslyOpenDialog(false);
      }
    },
    focusTrapSummaryDialog() {
      const dialog = document.querySelector('#customization-summary-dialog');
      this.focusableElements = getAllFocusableElements(dialog);
    },
    handleKeydown(event) {
      focusTrap(event, this.focusableElements);
    },
    showBuildSummary() {
      this.setSummaryDialogView(SUMMARY_DIALOG_VIEWS.BUILD_SUMMARY);
      this.clearQueuedActions();
    },
    clearQueuedActions() {
      this.setHasQueuedSaveAction(false);
      this.setHasQueuedSubmitToRetailerAction(false);
      this.setQueuedShareAction('');
    },
  },
};
</script>

<style lang="scss" scoped>
#customization-summary-dialog {
  overflow: hidden;
}

.customization-dialog-BUILD_SUMMARY,
.customization-dialog-SUBMIT_TO_RETAILER {
  :deep(.pdl-dialog) {
    display: flex;
    flex-direction: column;
    max-height: 90dvh;
  }

  :deep(.pdl-dialog__body) {
    padding: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    flex: 1;
  }
}
</style>
